import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext } from 'react';
import {
	deleteEDocumentQuery,
	eDocumentsQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useConfiguration,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import type { EDocumentsContext } from './context.types';

const ProfileCertificatesContext = createContext<EDocumentsContext>({
	documents: [],
	initialLoading: true,
	isDeleting: false,
	onDeleteEDocument: () => null,
});

interface ProfileEDocumentsContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
}

export const ProfileEDocumentsContextProvider = ({
	code,
	children,
}: ProfileEDocumentsContextProviderProps) => {
	const queryClient = useQueryClient();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { useDummyDataForEdocuments } = useConfiguration();
	const { axiosInstance } = useAxiosInstance();

	const eDocuments = usePoQuery({
		queryKey: ['edocuments'],
		queryFn: async () =>
			eDocumentsQuery(axiosInstance)(
				useDummyDataForEdocuments ? { text: 'DUMMY' } : undefined
			),
	});

	const deleteEDocument = usePoMutation({
		mutationFn: deleteEDocumentQuery(axiosInstance),
		onSuccess: async () => {
			toastMessageSuccess(tsn('formular.zprava.odebrano'));
			await queryClient.invalidateQueries({ queryKey: ['edocuments'] });
		},
	});

	return (
		<ProfileCertificatesContext.Provider
			value={{
				documents: eDocuments.data?.seznam ?? [],
				initialLoading: eDocuments.isLoading,
				onDeleteEDocument: (id, onSettled) =>
					deleteEDocument.mutate(id, {
						onError: () =>
							toastMessageError(tsn('formular.zprava.nepovedlo-se-odebrat')),
						onSettled,
					}),
				isDeleting: deleteEDocument.isLoading,
			}}>
			{children}
		</ProfileCertificatesContext.Provider>
	);
};

export const useProfileEDocumentsContextInstance = (): EDocumentsContext =>
	useContext(ProfileCertificatesContext);
